import { useState } from 'react'
import { t } from '../i18n'
import { formatPoints, pointsForAmount, pressKey, type KeypadMode } from './marketplace-model'

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', ',', '0', '⌫'] as const

/** "Attribution manuelle" — manual point attribution (Figma 2260:383).
 *
 * The cashier types what the customer spent and Badger converts it, or types points directly.
 * Needed whenever the POS can't attribute automatically: no Lightspeed link, an offline register,
 * or a customer owed points after the fact.
 *
 * The conversion rule is shown on screen precisely because it is NOT negotiable — 1 CHF = 10 pts
 * is fixed for every merchant (Points Algo §1), so the cashier can always explain the number.
 */
export function KeypadScreen({
  customerName,
  busy = false,
  onAttribute,
  onCancel,
}: {
  customerName: string
  busy?: boolean
  onAttribute: (points: number) => void
  onCancel: () => void
}) {
  const [mode, setMode] = useState<KeypadMode>('chf')
  const [entry, setEntry] = useState('')

  const points = pointsForAmount(entry, mode)
  // A zero entry attributes nothing, so it is not a valid submission — guard the button rather
  // than recording a no-op transaction the merchant later has to explain.
  const canSubmit = points > 0 && !busy

  function switchMode(next: KeypadMode) {
    if (next === mode) return
    // Clearing on switch is deliberate: "37,50" means francs, and silently reinterpreting it as
    // 37.5 points (or 3750) would attribute a wrong amount that looks plausible.
    setMode(next)
    setEntry('')
  }

  return (
    <div className="flex h-full flex-col bg-canvas">
      <header className="flex shrink-0 items-center justify-between border-b-[1.5px] border-line bg-white px-9 pt-[var(--ls-safe-top)] h-[calc(72px+var(--ls-safe-top))]">
        <h1 className="text-[22px] font-semibold text-ink">{t('manualAttribution')}</h1>
        <img src="/brand/badger-wordmark.png" alt="Badger" className="h-[30px] w-auto" />
      </header>

      <p className="shrink-0 px-9 pt-5 text-[15px] text-ink-soft">{customerName} · {t('linked')}</p>

      <div className="grid min-h-0 flex-1 grid-cols-2 gap-10 px-9 pt-3">
        <div className="min-w-0">
          <div className="rounded-xl border border-line bg-white p-7">
            <p className="text-[13px] font-semibold tracking-[1.04px] text-ink-soft">
              {mode === 'chf' ? t('amountSpent') : t('pointsToGive')}
            </p>
            <p className="mt-2 text-[46px] font-semibold leading-none text-ink">
              {entry || '0'} {mode === 'chf' ? 'CHF' : t('pts')}
            </p>
            {mode === 'chf' && (
              <p className="mt-4 text-[22px] font-semibold text-brand">
                = {formatPoints(points)} {t('points').toLowerCase()}
              </p>
            )}

            <div
              role="group"
              aria-label={t('entryMode')}
              className="mt-7 inline-flex rounded-full bg-canvas p-1"
            >
              {(['chf', 'points'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={mode === m}
                  onClick={() => switchMode(m)}
                  className={`rounded-full px-7 py-2.5 text-[17px] font-semibold transition ${
                    mode === m ? 'bg-brand text-white' : 'text-ink-soft'
                  }`}
                >
                  {m === 'chf' ? 'CHF' : t('points')}
                </button>
              ))}
            </div>
          </div>
          <p className="mt-4 text-[14px] text-ink-soft">{t('fixedRule')}</p>
        </div>

        <div className="min-w-0">
          <div className="grid grid-cols-3 gap-3.5">
            {KEYS.map((k) => (
              <button
                key={k}
                type="button"
                aria-label={k === '⌫' ? t('backspace') : k}
                onClick={() => setEntry((e) => pressKey(e, k, mode))}
                className="h-[76px] rounded-lg border border-line bg-white text-[26px] font-semibold text-ink transition hover:bg-canvas active:scale-[0.97]"
              >
                {k}
              </button>
            ))}
          </div>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onAttribute(points)}
            className="mt-6 h-[68px] w-full rounded-lg bg-brand text-[21px] font-semibold text-white transition hover:bg-brand-dark disabled:opacity-40"
          >
            {t('attributePoints')}
          </button>
        </div>
      </div>

      <footer className="shrink-0 px-9 pb-8 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="h-[58px] w-[182px] rounded-lg border border-line bg-white text-[18px] font-medium text-ink-soft transition hover:bg-canvas"
        >
          {t('cancel')}
        </button>
      </footer>
    </div>
  )
}
